import config from "config"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { alpha } from "@mui/material"

/**
 * Instruction from mui doc
 * ==============================
 * Ref: https://mui.com/material-ui/customization/theming/
 * Theme composition: using theme options to define other options
 * When the value for a theme option is dependent on another theme option, you should compose the theme in steps.
 */

export const venueMobileUIThemeOptionFactory = (theme) => ({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          userSelect: config("env") === "production" ? "none" : "auto",
        },
        "@keyframes pulse": {
          "0%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.6,
          },
          "100%": {
            opacity: 1,
          },
        },
        "@keyframes ripple": {
          from: {
            opacity: 1,
            transform: "scale3d(0.75,0.75,1)",
          },
          to: {
            opacity: 0,
            transform: "scale3d(1.6,1.6,1)",
          },
        },
        "@keyframes slideUp": {
          from: {
            transform: "translateY(100%)",
          },
          to: {
            transform: "translateY(0%)",
          },
        },
        "@keyframes slideDown": {
          from: {
            transform: "translateY(-100%)",
          },
          to: {
            transform: "translateY(0%)",
          },
        },
        "@keyframes maptalksfadeIn": {
          from: {
            opacity: 0,
          },

          to: {
            opacity: 1,
          },
        },
        "@keyframes highlightTag": {
          "100%": {
            backgroundPosition: "-100% 0",
          },
        },
        ".mtk-occupant-text-marker": {},
        /**
         * "mtk-ui-fadein" is the build class name for handling UIComponent fade-in animation in maptalks.js.
         * [@Soruce](https://github.com/maptalks/maptalks.js/blob/2a4ab207619543237a2943bbb1c293863c6a98bd/src/ui/UIComponent.ts#L201)
         * [@Style](https://github.com/maptalks/maptalks.js/blob/2a4ab207619543237a2943bbb1c293863c6a98bd/assets/maptalks.css#L202)
         */
        ".mtk-ui-fadein": {
          animation: "maptalksfadeIn 0.5s",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
      variants: [
        {
          props: { variant: "logo" },
          style: {
            borderRadius: 8,
            width: 55,
            height: 55,
            marginRight: theme.spacing(2.25),
            "& .MuiAvatar-img": {
              objectFit: "contain",
            },
          },
        },
        {
          props: { variant: "circular-logo" },
          style: {
            borderRadius: "50%",
            width: 80,
            height: 80,
            marginRight: theme.spacing(2.25),
            "& .MuiAvatar-img": {
              objectFit: "cover",
            },
          },
        },
        {
          props: { color: "primary" },
          style: {
            background: theme.palette.primary.main,
            borderRadius: 5,
            "& .MuiSvgIcon-root": {
              width: 20,
            },
          },
        },
      ],
    },
    MuiCard: {
      defaultProps: {
        elevation: 15,
      },
      styleOverrides: {
        root: {
          borderRadius: "0",
          color: "#000000",
        },
      },
      variants: [
        {
          props: { variant: "image" },
          style: {
            borderRadius: `${(theme.shape.borderRadius * 2) / 3}px`,
            "& .MuiCardActionArea-root:after": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.84))",
            },
            "& .MuiCardMedia-root": {
              height: "140px",
            },
            "& .MuiCardHeader-root": {
              position: "absolute",
              padding: theme.spacing(0, 1),
              minHeight: "60px",
              width: "100%",
              bottom: "0px",
              zIndex: 1,
              "& .MuiCardHeader-content": {
                color: "white",
              },
              "& .MuiCardHeader-title": {
                lineHeight: 1,
              },
              "& .MuiAvatar-root": {
                borderRadius: `${(theme.shape.borderRadius * 2) / 3}px`,
                width: "45px",
                height: "45px",
              },
              "& .MuiCardHeader-avatar": {
                marginRight: theme.spacing(1),
              },
              "& .MuiCardHeader-subheader": {
                fontSize: "0.75rem",
                color: "white",
              },
            },
          },
        },
        {
          props: { variant: "full-image" },
          style: {
            borderRadius: `${(theme.shape.borderRadius * 2) / 3}px`,
            "& .MuiCardActionArea-root:after": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.84))",
            },
            "& .MuiCardMedia-root": {
              height: "140px",
            },
            "& .MuiCardHeader-root": {
              position: "absolute",
              padding: theme.spacing(0, 1),
              minHeight: "60px",
              width: "100%",
              bottom: "0px",
              zIndex: 1,
              "& .MuiCardHeader-content": {
                color: "white",
              },
              "& .MuiCardHeader-title": {
                lineHeight: 1,
              },
              "& .MuiAvatar-root": {
                borderRadius: `${(theme.shape.borderRadius * 2) / 3}px`,
                width: "45px",
                height: "45px",
              },
              "& .MuiCardHeader-avatar": {
                marginRight: theme.spacing(1),
              },
              "& .MuiCardHeader-subheader": {
                fontSize: "0.75rem",
                color: "white",
              },
            },
          },
        },
      ],
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          whiteSpace: "pre",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      variants: [
        {
          props: { variant: "medium" },
          style: {
            "& .MuiListItemText-primary": {
              fontSize: theme.typography.body2.fontSize,
            },
            "& .MuiListItemText-secondary": {
              fontSize: theme.typography.caption.fontSize,
            },
          },
        },
        {
          props: { color: "primary" },
          style: {
            "& .MuiListItemText-primary": {
              color: theme.palette.primary.main,
            },
          },
        },
      ],
    },
    MuiRadio: {
      defaultProps: {
        icon: <CheckCircleIcon />,
        checkedIcon: <CheckCircleIcon />,
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "outlined" },
          style: {
            "&:hover": {
              background: theme.palette.grey[50],
              border: `1px solid ${theme.palette.grey[400]}`,
              color: theme.palette.text.primary,
            },
          },
        },
        {
          props: { variant: "contained" },
          style: {
            "&:hover": {
              background: alpha(theme.palette.primary.main, 0.8),
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: "8px",
          boxShadow: "none",
        },
        startIcon: {
          marginRight: theme.spacing(1.25),
        },
        endIcon: {
          marginLeft: theme.spacing(1.25),
        },
        sizeSmall: {
          padding: theme.spacing(1, 3),
        },
        sizeMedium: {},
        sizeLarge: {},
        outlinedSizeLarge: {
          borderWidth: "2px",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderBottomWidth: theme.spacing(0.25),
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        default: {
          background: "#fff",
        },
        sizeLarge: {},
      },
      variants: [
        {
          props: { variant: "rounded" },
          style: {
            borderRadius: 12,
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": {
            background: theme.palette.primary.dark,
          },
          "& .MuiSvgIcon-root": {
            "& path": { fill: theme.palette.primary.contrastText },
          },
        },
      },
      variants: [
        {
          props: { variant: "contained" },
          style: {
            borderRadius: 5,
          },
        },
      ],
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { gap: theme.spacing(1.25), paddingRight: theme.spacing(2) },
        grouped: {
          "&:not(:last-of-type)": {
            borderRadius: (theme.shape.borderRadius * 2) / 3,
          },
          "&:not(:first-of-type)": {
            marginLeft: 1,
            borderLeft: `1px solid rgba(203, 203, 203, 0.5)`,
            borderRadius: (theme.shape.borderRadius * 2) / 3,
          },
        },
      },
      variants: [
        {
          props: { variant: "borderSeperate" },
          style: {
            "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
              borderLeft: `1px solid transparent`,
            },
          },
        },
      ],
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: (theme.shape.borderRadius * 2) / 3,
          color: theme.palette.primary.main,
          borderColor: "rgba(203, 203, 203, 0.5)",
          "&.Mui-selected": {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              background: alpha(theme.palette.primary.main, 0.1),
              border: `1px solid ${theme.palette.grey[400]}`,
              color: theme.palette.text.primary,
            },
          },

          "&:hover": {
            background: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[400]}`,
            color: theme.palette.text.primary,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: theme.spacing(2),
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          ":hover": {
            background: alpha(theme.palette.primary.main, 0.1),
          },
        },
      },
    },

    /**
     * VenueUI (Vui)
     * Extra Component for venue.in.th projects
     */
    VuiDescriptionList: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
        labelText: {
          fontWeight: 700,
          fontSize: "12px",
          textTransform: "uppercase",
        },
        contentText: { fontWeight: 300 },
      },
    },
    VuiOnscreenKeyboard: {
      styleOverrides: {
        root: {
          // [Example] (https://hodgef.com/simple-keyboard/editor/?d=hodgef/react-simple-keyboard-demos/tree/uc-customization)
          // Add your custom style here
          "& .hg-theme-default": {
            fontFamily: "inherit",
          },
          "& .simple-keyboard": {
            maxWidth: "100%",
            padding: theme.spacing(8, 11.75),
            borderRadius: "20px",
            backgroundColor: theme.palette.grey[200],
            "&.hg-theme-default .hg-row": {
              marginBottom: theme.spacing(1.75),
              "&:last-child": {
                marginBottom: 0,
              },
              "& .hg-button:not(:last-child)": {
                marginRight: theme.spacing(1.75),
              },
            },
            "& .hg-button": {
              height: 65,
              backgroundColor: "#333333",
              color: theme.palette.common.white,
              fontSize: "32px",
              fontWeight: 500,
              borderRadius: "8px",
              "&>span": {
                display: "flex",
                fontSize: "inherit",
              },
            },
            "& .hg-activeButton": {
              backgroundColor: theme.palette.grey[300],
              color: theme.palette.text.primary,
            },
            "& .hg-button-number,& .hg-button-symbol, & .hg-button-abc,& .hg-button-lang":
              {
                fontSize: "12px",
                flexGrow: 1,
              },
            "& .hg-button-lang, & .hg-button-number, & .hg-button-abc": {
              flexGrow: 0.5,
            },
            "& .hg-button-shift,& .hg-button-bksp": {
              fontSize: "20px",
            },
            "& .hg-button-shift": {
              fontSize: "20px",
              flexGrow: 2,
              flexShrink: 0,
            },
            "& .hg-button-enter": {
              flexGrow: 2,
            },
            "& .hg-button-space": {
              flexGrow: 8,
            },
            "& .hg-primary-button": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
            "& .hg-secondary-button": {},
            "& .hg-small-button": {},
          },
        },
      },
    },
    VuiSearchToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: `9999px`,
          background: theme.palette.background.paper,
          width: "100%",
          padding: theme.spacing(1, 2.5),
          justifyContent: "flex-start",
          border: `1px solid #ececec`,
        },
        searchIcon: {
          color: theme.palette.common.black,
          fontSize: "1.125rem",
          marginRight: theme.spacing(2.5),
        },
        label: {
          color: theme.palette.text.secondary,
        },
      },
    },
    VuiSearchBasePopup: {
      styleOverrides: {
        drawer: {
          width: 350,
          display: "flex",
          flexDirection: "column",
          "& .VuiSearchPopupContent-closeButton--bottom": {
            display: "none",
          },
        },
        drawerPaper: {
          overflow: "hidden",
          width: "100%",
          [theme.breakpoints.up("sm")]: {
            width: 400,
          },
        },
        dialog: {
          "& .MuiDialogContent-root": {
            [theme.breakpoints.down("sm")]: {
              padding: theme.spacing(1.25, 2),
            },
            [theme.breakpoints.up("md")]: {
              padding: 0,
              overflowY: "hidden",
            },
          },
          [theme.breakpoints.up("md")]: {
            background: "#0000003D",
            zIndex: 1302,
          },
        },
        dialogPaper: {
          alignSelf: "flex-end",
          [theme.breakpoints.up("md")]: {
            width: 440,
            maxWidth: 440,
            alignSelf: "flex-start",
            background: "transparent",
            boxShadow: "none",
            marginTop: theme.spacing(1.5),
            borderRadius: "20px",
            overflowY: "hidden",
          },
        },
        dialogContent: {},
        dialogBackdrop: {},
      },
    },
    VuiSearchPopupContent: {
      styleOverrides: {
        root: {
          display: "flex",
          padding: theme.spacing(3.125, 3.125, 1.5, 3.125),
          width: "auto",
          gap: theme.spacing(2.5),
          alignItems: "center",
          backgroundColor: theme.palette.grey[50],
          color: theme.palette.text.primary,
          [theme.breakpoints.up("md")]: {
            backgroundColor: "transparent",
            padding: 0,
          },
        },
        closeButton: {
          padding: 0,
          width: "32px",
          height: "32px",
          [theme.breakpoints.up("md")]: {
            display: "none",
          },
        },
        closeButtonIcon: {
          color: "inherit",
        },
        searchInput: {
          backgroundColor: theme.palette.common.white,
          borderRadius: 9999,
          padding: theme.spacing(1.25, 2),
          fontSize: "1rem",
          gap: theme.spacing(2),
          fontWeight: 400,
          "& input": {
            padding: 0,
          },
          "& .MuiSvgIcon-root": {
            fontSize: "1.125rem",
            color: theme.palette.text.primary,
          },
          "& .MuiInputAdornment-root": {
            "&.MuiInputAdornment-positionEnd": {
              "& .MuiSvgIcon-root": {
                width: 8,
                height: 8,
              },
            },
          },
          [theme.breakpoints.up("md")]: {
            width: 440,
            maxWidth: 440,
          },
        },
        suggestedList: {
          marginTop: theme.spacing(2),
        },
        venueFilterContainer: {
          display: "none",
        },
      },
    },
    VuiAdSuggestList: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1, 2),
          backgroundColor: "#F2F2F2",
          margin: theme.spacing(0, 3),
          borderRadius: "16px",
        },
        suggestTitle: {
          fontSize: "1rem",
          fontWeight: 700,
        },
        suggestList: {
          "& .splide__track": {
            margin: 0,
            padding: theme.spacing(2, 0, 0.5),
          },
        },
        suggestListItem: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          justifyContent: "center",
          alignItems: "center",
        },
        suggestItemAvatar: {
          marginRight: 0,
        },
        suggestItemContent: {
          marginTop: theme.spacing(0.5),
          "& .MuiTypography-root": {
            fontSize: "0.625rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            textAlign: "center",
          },
        },
      },
    },
    VuiSearchQuickLink: {
      styleOverrides: {
        root: {
          pb: 1,
          mb: 1,
          mx: -2,
          px: 2,
        },
        stack: {
          pb: 2,
        },
        item: {
          boxShadow: "rgb(0 0 0 / 10%) 0px 2px 10px",
          border: "none",
          background: "white",
          "& .MuiSvgIcon-root": {
            width: "16px",
            height: "16px",
          },
        },
      },
    },
    VuiSearchResult: {
      styleOverrides: {
        root: {
          flexGrow: 1,
          width: "100%",
          overflow: "hidden auto",
          [theme.breakpoints.up("md")]: {
            marginTop: theme.spacing(1),
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow:
              "1px 1px 4px 0px #00000012, 5px 6px 8px 0px #0000000F, 12px 13px 11px 0px #0000000A, 21px 23px 13px 0px #00000003, 33px 36px 14px 0px #00000000",
            borderRadius: "20px",
          },
        },
        rootDialog: {
          height: "300px",
          [theme.breakpoints.up("md")]: {
            height: "auto",
            minHeight: 172,
            maxHeight: "calc(100vh - 100px)",
            overflowY: "auto",
          },
        },
        list: {
          padding: theme.spacing(2.75, 0),
        },
        listItem: {
          padding: theme.spacing(1, 3.125),
        },
      },
    },
    VuiSearchResultItem: {
      styleOverrides: {
        chip: {
          borderRadius: "24px",
          height: "20px",
          fontWeight: 400,
          backgroundColor: theme.palette.grey[200],
          color: theme.palette.text.secondary,
          fontSize: "0.625rem",
          width: "fit-content",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: theme.spacing(0.5, 1),
          "& .MuiChip-label": {
            fontSize: "0.75rem",
            lineHeight: "normal",
            padding: 0,
          },
        },
        primaryWrapper: {
          display: "flex",
          alignItems: "flex-start",
          gap: theme.spacing(0.75),
        },
        maintenanceIcon: {
          // TODO:: Remove `display: none` when standard maintenance design is available.
          display: "none",
          position: "relative",
          bottom: theme.spacing(-0.5),
          width: "18px",
          height: "18px",
          marginRight: theme.spacing(0.5),
        },
        newShopChip: {
          display: "none",
        },
        comingSoonChip: {
          display: "none",
        },
      },
    },
    VuiNoSearchResult: {
      styleOverrides: {
        root: {
          display: "flex",
          height: "300px",
          justifyContent: "center",
          alignItems: "center",
          [theme.breakpoints.up("md")]: {
            height: 172,
          },
        },
        noResultText: {},
      },
    },
    VuiSearchKeyboard: {
      styleOverrides: {
        root: {},
      },
    },
    VuiDetailCard: {
      styleOverrides: {
        pageLayout: {
          minHeight: "auto",
          "& .AppPageLayout-Container": {
            backgroundColor: theme.palette.background.paper,
          },
          "&.no-location": {
            paddingBottom: 0,
          },
          "& .Header-Container": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            "& .AppHeader-root": {
              backgroundColor: theme.palette.background.paper,
              "& .AppHeader-backbutton": {
                display: "flex",
                [theme.breakpoints.up("md")]: {
                  display: "none",
                },
              },
              "& .AppHeader-share": {
                display: "flex",
                [theme.breakpoints.up("md")]: {
                  display: "none",
                },
              },
              "& .AppHeader-title": {
                color: theme.palette.grey[800],
                fontWeight: 700,
                lineHeight: "112%",
              },
            },
          },
          "& .AppPageLayout-NavigationContainer": {
            display: "none",
            [theme.breakpoints.up("md")]: {
              display: "block",
            },
          },
        },
        card: {
          backgroundColor: "transparent",
          paddingTop: theme.spacing(1.5),
          [theme.breakpoints.up("md")]: {
            display: "flex",
            maxWidth: theme.breakpoints.values.lg,
            gap: theme.spacing(5),
            padding: theme.spacing(3, 3, 0, 3),
            margin: "0 auto",

            height: "calc(100vh - 65px)",
          },
        },
        infoContainer: {
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.default,
          borderRadius: "8px",
          padding: theme.spacing(2, 1),
          marginBottom: theme.spacing(2.5),
          gap: theme.spacing(2),
          "& .MuiDivider-root": {
            margin: 0,
            height: "1px",
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        },
        infoListItem: {
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(1),
          alignItems: "center",
          "& .InfoItem-title": {
            color: theme.palette.text.secondary,
            fontSize: "0.825rem",
            lineHeight: "128%",
          },
          "& .InfoItem-primary": {
            color: theme.palette.text.primary,
            fontSize: "0.825rem",
            fontWeight: 700,
            lineHeight: "112%",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            maxWidth: "fit-content",
            wordBreak: "break-word",
            textWrap: "nowrap",
            "& .MuiTypography-root": {
              color: theme.palette.text.primary,
              fontSize: "0.825rem",
              fontWeight: 700,
              lineHeight: "112%",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              maxWidth: "fit-content",
              wordBreak: "break-word",
              textWrap: "nowrap",
            },
          },
        },
        infoDivider: {
          "&:first-child": {
            display: "none",
          },
        },
        contentWrapper: {
          position: "relative",
          flex: 1,
          [theme.breakpoints.up("md")]: {
            width: "50%",
          },
        },
        cardHeader: {
          gap: theme.spacing(1),
          padding: 0,
          paddingBottom: theme.spacing(2.5),
          wordBreak: "break-word",
          "& .MuiCardHeader-title": {
            color: theme.palette.grey[800],
            fontWeight: 700,
            fontSize: "1rem",
            lineHeight: "112%",
            display: "-webkit-box",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 3,
            textOverflow: "ellipsis",
            [theme.breakpoints.up("md")]: {
              fontSize: "1.2rem",
              fontWeight: 600,
              lineHeight: "120%",
            },
          },
          "& .MuiCardHeader-action": {
            marginRight: 0,
          },
        },
        cardHeaderActionButton: {
          borderRadius: "8px",
          padding: theme.spacing(1),
          width: 40,
          height: 40,
          "& .MuiSvgIcon-root": {
            width: 24,
            height: 24,
            "& path": {
              fill: "#C9C5C6",
            },
          },

          backgroundColor: "transparent",
          border: "1px solid #E4E2E3",
          "&:hover": {
            backgroundColor: "#E4E2E3",
          },
        },
        cardContent: {
          padding: 0,
          paddingTop: theme.spacing(0.5),
          marginBottom: theme.spacing(2),
          "&:last-child": {
            paddingBottom: 0,
          },
        },
        cardContentPrimaryText: {
          color: theme.palette.text.primary,
          fontWeight: 700,
          fontSize: "0.825rem",
          lineHeight: "112%",
          marginBottom: theme.spacing(1),
        },
        cardContentSecondaryText: {
          color: theme.palette.text.secondary,
          fontSize: "0.825rem",
          lineHeight: "112%",
          wordBreak: "break-word",
          [theme.breakpoints.up("md")]: {
            maxHeight: "530px",
            overflow: "hidden auto",
            paddingBottom: theme.spacing(3),
          },
        },
        galleryCarouselWrapper: {
          margin: "0 auto",
          width: "100%",
          [theme.breakpoints.up("sm")]: {
            width: "80%",
          },
          marginBottom: theme.spacing(1.5),
          [theme.breakpoints.up("md")]: {
            width: "50%",
            flex: 1,
          },
        },
        galleryCarousel: {
          "& .splide__track": {
            padding: 0,
            margin: 0,
          },
          "& .splide__pagination": {
            position: "absolute",
          },
          [theme.breakpoints.up("sm")]: {
            width: "40%",
            margin: "0 auto",
          },
          [theme.breakpoints.up("md")]: {
            width: "100%",
            margin: 0,
            "& .splide__pagination": {
              bottom: -20,
            },
          },
        },
        galleryCarouselItem: {
          width: "100%",
          objectFit: "cover",
          borderRadius: "12px",
          aspectRatio: "343 / 430",
        },
        cardHeaderAction: {
          display: "flex",
          columnGap: theme.spacing(1),
          marginLeft: theme.spacing(4.75),
        },
        viewLocationContainer: {
          position: "fixed",
          bottom: 0,
          right: 0,
          display: "flex",
          width: "100%",
          padding: theme.spacing(2, 2, 4, 2),
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.grey[200]}`,
          [theme.breakpoints.up("sm")]: {
            padding: theme.spacing(2, 3, 4, 3),
          },
          [theme.breakpoints.up("md")]: {
            position: "absolute",
            bottom: 0,
            width: "100%",
            backgroundColor: theme.palette.background.paper,
            borderTop: "none",
            padding: theme.spacing(1.5, 0, 4, 0),
          },
        },
        viewLocationButton: {
          width: "100%",
          padding: theme.spacing(1.25, 2),
          borderRadius: "12px",
          fontSize: "1rem",
          lineHeight: "1",
          fontWeight: 400,
          "& .MuiButton-startIcon": {
            marginRight: theme.spacing(0.8125),
          },
          [theme.breakpoints.up("md")]: {
            fontSize: "1rem",
            height: "46px",
          },
        },
        scrollTriggerWrapper: {
          position: "fixed",
          bottom: "80px",
          width: "100%",
          padding: theme.spacing(0, 3),
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.grey[200]}`,
          boxShadow: theme.shadows[1],
          display: "block",
        },
        websiteContainer: {
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(1),
          marginBottom: theme.spacing(2),
        },
        websiteTitle: {
          color: theme.palette.text.primary,
          fontWeight: 700,
          fontSize: "0.825rem",
          lineHeight: "112%",
        },
        websiteLinkText: {
          color: "#4792DA",
          fontSize: "0.825rem",
          lineHeight: "112%",
        },
      },
    },
    VuiCarousel: {
      styleOverrides: {
        root: {
          "& .splide__pagination__page": {
            background: theme.palette.grey[500],
          },
          "& .splide__pagination__page.is-active": {
            background: theme.palette.primary.main,
          },
          "& .splide__pagination": {
            position: "static",
          },
          "& .splide__track": {
            overflow: "hidden",
            margin: theme.spacing(-3),
            padding: theme.spacing(3),
          },
          "& .splide__arrow": {
            background: "none",
            margin: theme.spacing(0, -5),
            "& svg": {
              fill: theme.palette.grey[500],
            },
          },
        },
      },
    },
    HorizontalScrollPane: {
      styleOverrides: {
        root: {
          width: "100%",
          "& .simplebar-track.simplebar-horizontal": {
            display: "none",
          },
        },
        listWrapper: {
          width: "100%",
          padding: theme.spacing(1),
        },
      },
    },
    LoadingScreen: {
      styleOverrides: {
        root: {
          position: "fixed",
          width: "100dvw",
          height: "100dvh",
          backgroundColor: theme.palette.common.white,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
        },
        logo: {
          maxWidth: "70dvw",
        },
      },
    },
    FeatureChipToggleBar: {
      styleOverrides: {
        root: {
          padding: 0,
          borderRadius: 0,
        },
        button: {
          padding: theme.spacing(0.5),
          borderRadius: "16px",
          boxShadow: "none",
          background: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[200]}`,
          whiteSpace: "nowrap",
          height: "32px",
          color: theme.palette.text.primary,
          fontWeight: 400,
          "&.MuiToggleButtonGroup-grouped:not(:last-of-type)": {
            borderRadius: "16px",
            margin: 0,
          },
          "&.MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            border: `1px solid ${theme.palette.grey[200]}`,
            margin: 0,
            borderRadius: "16px",
          },
          "&.MuiToggleButtonGroup-grouped:first-of-type": {
            marginLeft: theme.spacing(3),
          },
          "&.Mui-selected": {
            "& svg": {
              fill: theme.palette.primary.contrastText,
            },
          },
          "& svg": {
            width: 16,
            height: 16,
            fill: theme.palette.primary.main,
            marginLeft: theme.spacing(0.5),
            marginRight: "-6px",
          },
        },
        text: {
          fontSize: "0.8125rem",
          padding: theme.spacing(0, 1.5),
          letterSpacing: "0.00938em",
        },
      },
    },
    VuiAdModal: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        },
        closeButton: {
          padding: theme.spacing(0.5, 2),
          marginTop: theme.spacing(2),
        },
      },
    },
    VuiAdImageCarousel: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
        carousel: {
          "& .splide__pagination": {
            marginTop: theme.spacing(0.5),
          },
          "& .splide__pagination__page": {
            width: "8px",
            height: "8px",
          },
          "& .splide__pagination__page.is-active": {
            backgroundColor: theme.palette.primary.main,
            transition: `width ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}, borderRadius ${theme.transitions.duration.standard}s ${theme.transitions.easing.easeInOut}`,
            transform: "none",
            width: "24px",
            borderRadius: "30px",
          },
        },
        carouselItem: {
          display: "flex",
          justifyContent: "center",
        },
        image: {
          minWidth: "320px",
          maxWidth: "500px",
          aspectRatio: "3 / 4",
          objectFit: "cover",
          borderRadius: "16px",
        },
      },
    },
    VuiAnnoucementCard: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(1),
        },
        closeButtonContainer: {
          display: "flex",
          justifyContent: "flex-end",
        },
        closeButton: {
          backgroundColor: "rgba(255, 255, 255, 0.50)",
          borderRadius: "50%",
          padding: 0,
          minWidth: "24px",
          "& .MuiSvgIcon-root": {
            width: "50%",
          },
          cursor: "pointer",
          "&.MuiButtonBase-root:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.50)",
          },
        },
        detailCard: {
          display: "flex",
          padding: theme.spacing(2),
          gap: theme.spacing(1),
          borderRadius: "16px",
        },
        cardContent: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          "&.MuiCardContent-root:last-child": {
            paddingBottom: 0,
          },
        },
        cardMedia: {
          aspectRatio: "1/1",
          width: "30%",
          borderRadius: "8px",
          objectFit: "cover",
        },
        titleContainer: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          color: theme.palette.primary.main,
        },
        locationContainer: {
          display: "flex",
          gap: theme.spacing(1),
        },
        iconWrapper: {
          width: "16px",
          height: "16px",
          "& .MuiSvgIcon-root": {
            height: "100%",
            width: "100%",
          },
        },
      },
    },
  },
})
